/**
 * cizilecek.jsx — "Çizilecek" (To be Drawn) stands: the badges, the box and the prompts of the Stand-Drawing flow
 * (backlog #653, #655, #658; backend #654, fdsVector #652 / #656 / #657).
 *
 * A stand is "to be drawn" when it is placed in a hall at the fair and no shape of a saved plan is linked to it. The counts
 * per hall come with GET /fuar/{fuarId}/h-max (`standCount`, `drawnStandCount`, `toDrawCount`); the list comes from
 * services/cizilecekStandService.js. Both are FDS-Backend's SAVED mirror of the plan, so they can lag the editor by seconds:
 * the box says when the plan was last saved and has a refresh button. Nothing here changes a status by itself.
 *
 *   cizilecekDurumu(hall)       → what the hall's badge says (waiting for the Salon Planı / unknown / no stands / all drawn / N)
 *   yenidenCizilecek(hall)      → N when a hall marked Tamamlandı has stands to draw again (a stand added after completion), else 0
 *   ilkStandAdlari(stands, n)   → the first names, for the completion warning
 *   CizilecekRozeti             → "N çizilecek" / "Tümü çizildi" next to a hall's stand count
 *   YenidenCizilecekIsareti     → the amber "Tamamlandı — N yeni çizilecek stand" marker, with "Çizime devam et" when given a handler
 *   CizilecekStandlarKarti      → the box of Krokiler: searchable list, "Çiz", refresh, last plan save
 *   AlanPlaniEngelPaneli        → the prominent prompt of the Alan Planı page while the Salon Planı is unfinished
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, Spinner, Table,
} from "reactstrap";
import { CircleCheck, RefreshCw, ExternalLink, PencilLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CIZIM_DURUM, CizimDurumuPill } from "./salonDurumlari";

export const CIZILECEK = {
  /** The Salon Planı is not Tamamlandı: no stand can be drawn yet, so no count is shown. */
  BEKLIYOR: "bekliyor",
  /** The backend did not send the counts (an older backend): nothing is claimed. */
  BILINMIYOR: "bilinmiyor",
  /** The hall has no stands at this fair. */
  STAND_YOK: "standYok",
  /** Stands exist and none is left to draw. */
  TAMAM: "tamam",
  /** N > 0 stands are left to draw. */
  VAR: "var",
};

const sayiOku = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/** @returns {{durum: string, sayi: number|null, toplam: number|null}} for a getFuarHMax → halls[i] (or a row built from it). */
export function cizilecekDurumu(hall) {
  if (!hall || hall.salonPlaniDurum !== CIZIM_DURUM.TAMAMLANDI) return { durum: CIZILECEK.BEKLIYOR, sayi: null, toplam: null };
  const toplam = sayiOku(hall.standCount);
  const sayi = sayiOku(hall.toDrawCount);
  if (sayi == null || toplam == null) return { durum: CIZILECEK.BILINMIYOR, sayi: null, toplam };
  if (toplam === 0) return { durum: CIZILECEK.STAND_YOK, sayi: 0, toplam: 0 };
  return { durum: sayi === 0 ? CIZILECEK.TAMAM : CIZILECEK.VAR, sayi, toplam };
}

/**
 * Backlog #658: a hall whose Alan Planı is marked Tamamlandı but has stands to draw AGAIN (one was added or moved into the hall after
 * completion). The status never changes by itself — this only flags it. 0 = nothing to flag.
 */
export function yenidenCizilecek(hall) {
  if (!hall || hall.alanPlaniDurum !== CIZIM_DURUM.TAMAMLANDI) return 0;
  const sayi = sayiOku(hall.toDrawCount);
  return sayi != null && sayi > 0 ? sayi : 0;
}

/** The first {@code n} stand names of a to-draw list ("A-101, A-102, …"), and how many more there are. */
export function ilkStandAdlari(stands, n = 5) {
  const liste = stands || [];
  return {
    adlar: liste.slice(0, n).map((s) => s.standName ?? `#${s.standId}`),
    kalan: Math.max(0, liste.length - n),
  };
}

/** "N çizilecek" next to the stand count of a hall; "Tümü çizildi" when none is left; nothing while there is nothing to say. */
export function CizilecekRozeti({ hall, className = "", tamamGoster = true }) {
  const { t } = useTranslation("yonetim");
  const d = cizilecekDurumu(hall);
  if (d.durum === CIZILECEK.VAR) {
    return (
      <Badge color="warning" pill className={className} title={t("cizilecek.rozetAciklama")}>
        {t("cizilecek.rozet", { sayi: d.sayi })}
      </Badge>
    );
  }
  if (d.durum === CIZILECEK.TAMAM && tamamGoster) {
    return (
      <Badge color="success" pill className={className}>
        <CircleCheck size={11} className="me-1" />{t("cizilecek.tumuCizildi")}
      </Badge>
    );
  }
  return null;
}

/** Amber marker for a Tamamlandı Alan Planı with new stands to draw; `onDevam` adds "Çizime devam et" (status back to Çiziliyor). */
export function YenidenCizilecekIsareti({ hall, onDevam = null, busy = false, className = "" }) {
  const { t } = useTranslation("yonetim");
  const sayi = yenidenCizilecek(hall);
  if (sayi === 0) return null;
  return (
    <span className={`d-inline-flex align-items-center gap-2 ${className}`}>
      <Badge color="warning" pill className="text-dark border border-warning" title={t("cizilecek.yeniAciklama")}>
        {t("cizilecek.tamamlandiYeniCizilecek", { sayi })}
      </Badge>
      {onDevam && (
        <Button size="sm" color="warning" outline disabled={busy} onClick={onDevam}>
          <PencilLine size={13} className="me-1" />{t("cizilecek.cizimeDevamEt")}
        </Button>
      )}
    </span>
  );
}

const fmtZaman = (v, tag) => (v ? new Date(v).toLocaleString(tag) : null);

/**
 * The "Çizilecek Standlar" box of Krokiler (backlog #655). `liste` is the result of getCizilecekStandlar for the selected hall
 * ({ok, stands, lastPlanSaveAt, message}) or null while it loads.
 */
export function CizilecekStandlarKarti({
  hall, salonId, fuarAlaniId = null, liste = null, yukleniyor = false, onYenile = null,
  duzenleyebilir = false, alanPlaniEngelli = null, localeTag = "tr-TR",
}) {
  const { t } = useTranslation("yonetim");
  const [arama, setArama] = useState("");
  const salonPlaniTamam = hall?.salonPlaniDurum === CIZIM_DURUM.TAMAMLANDI;
  const stands = useMemo(() => (liste?.ok ? liste.stands : []), [liste]);
  const gorunen = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr");
    if (!q) return stands;
    return stands.filter((s) => [s.standName, s.firmaName, s.firmaShortName].some((v) => String(v ?? "").toLocaleLowerCase("tr").includes(q)));
  }, [stands, arama]);

  const salonPlaniLinki = fuarAlaniId != null && salonId != null
    ? `/Management/FairAreas/${fuarAlaniId}/Salonlar/${salonId}/Plan?katman=floorplan`
    : null;
  const sayi = liste?.ok ? stands.length : null;
  const sonKayit = liste?.ok ? fmtZaman(liste.lastPlanSaveAt, localeTag) : null;

  let govde;
  if (!salonPlaniTamam) {
    govde = (
      <div className="text-muted small py-2">
        {t("cizilecek.salonPlaniTamamlaninca")}{" "}
        {salonPlaniLinki && <Link to={salonPlaniLinki}>{t("cizilecek.salonPlaniniAc")}</Link>}
      </div>
    );
  } else if (yukleniyor || liste == null) {
    govde = <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>;
  } else if (!liste.ok) {
    govde = <Alert color="warning" className="py-2 small mb-0">{t("cizilecek.listeAlinamadi", { message: liste.message ?? "" })}</Alert>;
  } else if (stands.length === 0) {
    govde = (
      <div className="py-2">
        <div className="text-success"><CircleCheck size={15} className="me-1" />{t("cizilecek.tumStandlarCizildi")}</div>
        {hall?.alanPlaniDurum !== CIZIM_DURUM.TAMAMLANDI && (
          <div className="text-muted small mt-1">
            {t("cizilecek.tamamlandiIsaretleyin")}{" "}
            {duzenleyebilir && <Link to={`/Management/Salonlar/${salonId}/AlanPlani`}>{t("cizilecek.alanPlaninaGit")}</Link>}
          </div>
        )}
      </div>
    );
  } else {
    govde = (
      <>
        <Input bsSize="sm" type="text" className="mb-2" placeholder={t("cizilecek.standFirmaAra")} value={arama}
          onChange={(e) => setArama(e.target.value)} />
        {gorunen.length === 0 ? (
          <div className="text-muted text-center py-3 small">{t("cizilecek.aramaSonucYok")}</div>
        ) : (
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <Table size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("cizilecek.stand")}</th>
                  <th>{t("cizilecek.katilimci")}</th>
                  <th className="text-end">{t("cizilecek.alan")}</th>
                  <th className="text-end">{t("cizilecek.eylemler")}</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((s) => (
                  <tr key={s.standId}>
                    <td className="fw-semibold">{s.standName ?? `#${s.standId}`}</td>
                    <td className="small">{s.firmaShortName || s.firmaName || <span className="text-muted">—</span>}</td>
                    <td className="text-end small">{s.areaM2 != null ? `${s.areaM2} m²` : <span className="text-muted">—</span>}</td>
                    <td className="text-end">
                      <div className="d-inline-flex gap-1">
                        <Link to={`/Management/Standlar/${s.standId}`} className="btn btn-sm btn-light" title={t("cizilecek.standDetay")}>
                          <ExternalLink size={12} />
                        </Link>
                        {duzenleyebilir && (
                          alanPlaniEngelli ? null : (
                            <Link to={`/Management/Salonlar/${salonId}/AlanPlani?cizilecekStandId=${s.standId}`}
                              className="btn btn-sm btn-soft-primary" title={t("cizilecek.ciz")}>
                              <PencilLine size={12} className="me-1" />{t("cizilecek.ciz")}
                            </Link>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </>
    );
  }

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex align-items-center gap-2 flex-wrap py-2">
        <span className="fw-semibold flex-grow-1">
          {t("cizilecek.baslik")}{sayi != null && <>{" "}<Badge color="warning" pill className="ms-1">{sayi}</Badge></>}
        </span>
        {salonPlaniTamam && sonKayit && <span className="text-muted small">{t("cizilecek.sonPlanKaydi", { zaman: sonKayit })}</span>}
        {salonPlaniTamam && onYenile && (
          <Button size="sm" color="light" title={t("cizilecek.yenile")} disabled={yukleniyor} onClick={onYenile}>
            <RefreshCw size={13} />
          </Button>
        )}
      </CardHeader>
      <CardBody>{govde}</CardBody>
    </Card>
  );
}

/**
 * Backlog #653: shown on the Alan Planı page while the Salon Planı is not Tamamlandı. A prompt, not a tooltip: the Alan Planı cannot be
 * drawn yet; here are the Salon Planı status (the condition) and the Altyapı Planı status (information only, D2) and a button to open the
 * Salon Planı. `engel` is the backend's own reason (alanPlaniEngeli), so the wording stays in one place.
 */
export function AlanPlaniEngelPaneli({ hall, salonId, fuarAlaniId = null, engel }) {
  const { t } = useTranslation("yonetim");
  const salonPlaniLinki = fuarAlaniId != null && hall?.registeredAtFairground !== false
    ? `/Management/FairAreas/${fuarAlaniId}/Salonlar/${salonId}/Plan?katman=floorplan`
    : null;
  return (
    <Alert color="warning" className="mb-3">
      <h6 className="alert-heading mb-1">{t("cizilecek.alanPlaniHenuzCizilemez")}</h6>
      {engel && <div className="small mb-2">{engel}</div>}
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="small">{t("cizilecek.salonPlani")}:</span>
        <CizimDurumuPill id="engel-salon-plani" plan="salonPlani" durum={hall?.salonPlaniDurum ?? null} tarih={hall?.salonPlaniDurumTarihi} />
        <span className="small ms-2">{t("cizilecek.altyapiPlani")} <span className="text-muted">({t("cizilecek.yalnizcaBilgi")})</span>:</span>
        <CizimDurumuPill id="engel-altyapi-plani" plan="altyapiPlani" durum={hall?.altyapiPlaniDurum ?? null} tarih={hall?.altyapiPlaniDurumTarihi} />
        {salonPlaniLinki && (
          <Link to={salonPlaniLinki} className="btn btn-warning btn-sm ms-auto">{t("cizilecek.salonPlaniniAc")}</Link>
        )}
      </div>
    </Alert>
  );
}
